import { useEffect, useState } from "react";

type Props = {
    visible: boolean,
    onSave: () => void,
    onCancel: () => void,
    saving?: boolean,
    disabled?: boolean,
    alignWithSelector?: string,
    errorMessage?: string,
    onErrorDismiss?: () => void,
}

export default function StickySaveBar({ visible, onSave, onCancel, saving = false, disabled = false, alignWithSelector, errorMessage, onErrorDismiss }: Props) {
    const [leftOffset, setLeftOffset] = useState(0);

    useEffect(() => {
        if (!alignWithSelector) {
            setLeftOffset(0);
            return;
        }

        if (!visible) return;
        const element = document.querySelector(alignWithSelector);
        const update = () => {
            const rect = element?.getBoundingClientRect();
            setLeftOffset(rect && rect.width > 0 ? rect.right : 0);
        };
        update();
        window.addEventListener('resize', update);
        const observer = element ? new ResizeObserver(update) : null;
        if (element) observer?.observe(element);
        return () => {
            window.removeEventListener('resize', update);
            observer?.disconnect();
        };
    }, [visible, alignWithSelector]);

    useEffect(() => {
        if (!errorMessage || !onErrorDismiss) return;
        const timer = setTimeout(onErrorDismiss, 5000);
        return () => clearTimeout(timer);
    }, [errorMessage, onErrorDismiss]);

    const [renderedError, setRenderedError] = useState<string | null>(null);
    useEffect(() => {
        if (errorMessage) setRenderedError(errorMessage);
    }, [errorMessage]);

    const visibilityClass = visible ? 'is-visible' : '';
    const showError = visible && Boolean(errorMessage);

    return (
        <>
            <div className={`sticky-save-bar__spacer ${visibilityClass} ${showError ? 'has-error' : ''}`} />
            <div className={`sticky-save-bar ${visibilityClass}`} style={{ left: leftOffset }}>
                <div className={`sticky-save-bar__error ${showError ? 'is-visible' : ''}`}>
                    <span className="sticky-save-bar__error-message">{renderedError}</span>
                    <button
                        type="button"
                        className="sticky-save-bar__error-close"
                        onClick={onErrorDismiss}
                        aria-label="Dismiss error"
                    >
                        ×
                    </button>
                </div>
                <div className="sticky-save-bar__bar">
                    <div className="sticky-save-bar__status">
                        <span className="sticky-save-bar__title">Unsaved changes</span>
                        <span className="sticky-save-bar__hint">— your changes haven't been saved yet</span>
                    </div>
                    <div className="sticky-save-bar__actions">
                        <button
                            type="button"
                            className="sticky-save-bar__cancel"
                            onClick={onCancel}
                            disabled={saving || disabled}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="sticky-save-bar__save"
                            onClick={onSave}
                            disabled={saving || disabled}
                        >
                            {saving ? "Saving..." : "Save changes"}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
